import {
  CompRef,
  PresetsValue,
  WidgetDescriptor,
} from '@wix/document-services-types';
import { ComponentDefinition } from '@wix/editor-platform-sdk-types';
import { ExtendedPlatformContext } from '../../../../../types/platformApi';
import {
  AddToStageData,
  AppData,
  UnifiedWidget,
  BuilderComponent,
} from '../../../../../types/unifiedComponents';
import {
  AddUnifiedComponentsErrorCode as ErrorCode,
  createAddUnifiedComponentsError,
} from '../errors';
import { createLayouts, getWidgetAppearanceData } from './addBlocksWidget';
import { componentInitialSizeToUnitSize } from '../utils';

const TOKEN = 'unified-components-installation';

export async function addBuilderWidgetComponent(
  context: ExtendedPlatformContext,
  appData: AppData,
  widgetDescriptor: WidgetDescriptor,
  widgetDefinition: UnifiedWidget,
  containerRef: CompRef,
  addToStageData: AddToStageData,
): Promise<CompRef> {
  const { widgetId, componentModel } = widgetDefinition;
  const isStretched = !!widgetDefinition.isStretched?.desktop;
  const containerLayout =
    context.documentServices.components.layout.get(containerRef);

  const { presetId, layout, layouts } = getWidgetAppearanceData({
    widgetDefinition,
    widgetDescriptor,
    presetId: addToStageData.presetId,
    mobilePresetId: addToStageData.mobilePresetId,
    position: addToStageData.position,
    containerLayout,
    isStretched,
  });

  const componentType = componentModel?.componentType;

  const componentDefinition: ComponentDefinition = {
    componentType,
    type: 'Component',
    layout,
    layouts,
  };

  if (presetId) {
    componentDefinition.presets = {
      type: 'PresetData',
      layout: presetId,
      style: presetId,
    } as PresetsValue;
  }

  try {
    const compRef = await context.platformApiMethods.document.components.add(
      appData,
      TOKEN,
      {
        componentDefinition,
        pageRef: containerRef,
      },
    );

    return compRef;
  } catch (error) {
    throw createAddUnifiedComponentsError(
      ErrorCode.addingBuilderComponentFailed,
      'Could not add builder component',
    )
      .withBuilderComponentType(componentType)
      .withUserComponentId(widgetId, 'builder-component')
      .withParentError(error as Error);
  }
}

export async function addBuilderComponent(
  context: ExtendedPlatformContext,
  appData: AppData,
  builderComponentDefinition: BuilderComponent,
  containerRef: CompRef,
): Promise<CompRef> {
  const { data, componentId } = builderComponentDefinition;
  const containerLayout =
    context.documentServices.components.layout.get(containerRef);

  const initialSize =
    builderComponentDefinition?.data?.installation?.initialSize;
  const size = componentInitialSizeToUnitSize(initialSize, {
    width: { value: 11.225296442687746, type: 'percentage' },
    // @ts-expect-error - TODO: fix type in DM
    height: { value: 0.2943661971830986, type: 'aspectRatio' },
  });

  const componentType = data.type!;
  // TODO: Shouldn't we use getLayouts function?
  const layouts = createLayouts({
    type: 'ComponentLayout',
    width: size.width,
    height: size.height,
    minWidth: {
      type: 'px',
      value: 0,
    },
    minHeight: {
      type: 'unset',
    },
    maxWidth: {
      type: 'px',
      value: 99999,
    },
    maxHeight: {
      type: 'px',
      value: 99999,
    },
    hidden: false,
  });

  const componentDefinition: ComponentDefinition = {
    componentType,
    type: 'Component',
    layouts,
  };

  const presets = builderComponentDefinition?.data?.installation?.presets;

  if (presets) {
    if (presets.large) {
      componentDefinition.presets = {
        type: `${componentType}.Presets`,
        presetName: presets.large,
      };
    }

    if (presets.small) {
      // TODO: [EP-6783] Get an API from the Editors to get the mobile breakpoint ID
      const findSmallBreakpointId = () => {
        const pageRef = context.documentServices.pages.getCurrentPage();
        const breakpoints =
          context.documentServices.breakpoints.get(pageRef).values;
        return breakpoints.reduce((prev, curr) =>
          prev.canvasSize! < curr.canvasSize! ? prev : curr,
        ).id!;
      };

      const smallBreakpointId = findSmallBreakpointId();
      // @ts-expect-error - TODO: [EP-6782] fix type in DM
      componentDefinition.scopedPresets = {
        [smallBreakpointId]: {
          type: `${componentType}.Presets`,
          presetName: presets.small,
        },
      };
    }
  }

  try {
    return await context.platformApiMethods.document.components.add(
      appData,
      TOKEN,
      {
        componentDefinition,
        pageRef: containerRef,
      },
    );
  } catch (error) {
    throw createAddUnifiedComponentsError(
      ErrorCode.addingBuilderComponentFailed,
      'Could not add builder component',
    )
      .withBuilderComponentType(componentType)
      .withUserComponentId(componentId, 'builder-component')
      .withParentError(error as Error);
  }
}
